import React, { useState, useRef, useEffect } from 'react';
import AddIcon from '../../../../../../../assets/svg/solo-chat/circle-plus.svg';
import { DeleteIcon, EditIcon } from '../../../icons';
import style from './entity-value.module.scss';
import { Input } from '../../../components';
import { UpdateEntityValuePayload, api } from '../api';
import { useMutate, useAppAndBranchId, useCRUD, usePermissions } from '../../../hooks';
import { LoadingIcon } from '../../../icons/LoadingIcon';
import { useToast } from '../../../../../../../helpers/commonFunction';
import { Entity } from '../types';
import { errorMessage } from '../../../utils/error';

type Props = {
	entity: Entity;
	close: () => void;
	onSave: () => void;
};

export function EntityValue(props: Props) {
	const { ErrorToast } = useToast();
	const [appId, branchId] = useAppAndBranchId();
	const inputRef = useRef<HTMLInputElement>(null);
	const [isAdding, setIsAdding] = useState(false);
	const [error, setError] = useState('');
	const { entity, close, onSave } = props;
	const { mutate, loading } = useMutate(api.updateEntityValue);

	const valuesCRUD = useCRUD(entity.entityValues?.length ?? 0);
	const { actions, deleteList, isDirty, updateList } = valuesCRUD;
	const list =
		(entity.entityValues?.map((el) => el.name) ?? []).concat(valuesCRUD.list) ??
		[];
	const [edit, setEdit] = useState<{
		index: number;
		initialValue: string;
	}>({ index: -1, initialValue: '' });

	const [canCreate, canEdit, canDelete] = usePermissions(['nlu.Create', 'nlu.Edit', 'nlu.Delete']) as boolean[]

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.keyCode !== 13 || !inputRef.current) {
			return;
		}
		const value = e.currentTarget.value.trim();
		if (!validateEntityValue(value)) {
			return;
		}
		if (isAdding) {
			// adding new entity value
			actions.addItem(value);
		} else if (edit.index !== -1) {
			// editing existing entity value
			actions.updateItem(edit.index, value);
			setEdit({ index: -1, initialValue: '' });
		}

		inputRef.current.value = '';
	}

	function handleDelete(index: number) {
		actions.deleteItem(index);
	}

	function validateEntityValue(entityValue: string) {
		if (!entityValue.length) {
			setError('Entity value cannot be empty');
			return false;
		}
		const values = list
			.filter((_, index) => !deleteList.includes(index))
			.map((el, index) => {
				return updateList[index] ?? el;
			});
		if (values.includes(entityValue)) {
			setError('Entity value already exists');
			return false;
		}
		return true;
	}

	// getUpdatePayload produces the payload for the API
	// from the local state
	function getUpdatePayload() {
		const payload: UpdateEntityValuePayload = {};
		if (valuesCRUD.list.length) {
			// remove duplicates
			payload.create = valuesCRUD.list.filter((el) => {
				const index = (entity.entityValues ?? []).findIndex((val) => val.name === el);
				const isExists = index !== -1
				return !isExists;
			});
		}
		if (deleteList.length) {
			payload.delete = deleteList.map(
				(index) => entity.entityValues?.[index]?.id ?? '',
			);
		}
		if (Object.keys(updateList).length) {
			payload.update = {};
			Object.entries(updateList).forEach(([index, value]) => {
				const id = entity.entityValues?.[index as unknown as number]?.id;
				if (id) {
					(payload.update as Record<string, string>)[id] = value;
				}
			});
		}
		return payload;
	}

	// reset to default state as this component
	// can be rendered with different props without getting
	// unmounted
	useEffect(() => {
		setEdit({ index: -1, initialValue: '' });
		setIsAdding(false);
		setError('');
		actions.setOffset(entity.entityValues?.length ?? 0);
		actions.clear();
	}, [entity]);

	return (
		<div className={style.entityValue}>
			<div className={style.header}>
				<p>
					List - <span className={style.entity}>{entity.name}</span>
				</p>
				<div className={style.actions}>
					<button className={style.cancelBtn} onClick={close}>
						Cancel
					</button>
					{(canCreate || canDelete || canEdit) && (
						<button
							disabled={!isDirty}
							data-loading={loading}
							className={style.saveBtn}
							onClick={async () => {
								if (!loading) {
									try {
										const payload = getUpdatePayload();
										await mutate(appId, branchId, entity.id, payload);
										onSave();
										close();
									} catch (err) {
										ErrorToast(errorMessage("save", "entity values", err.code));
									}
								}
							}}
						>
							<span>Save</span>
							{loading && (
								<LoadingIcon width="14" height="14" stroke="#FFF" />
							)}
						</button>
					)}
				</div>
			</div>

			<div className={style.main}>
			 {canEdit && (
				<>
				{isAdding ? (
					<div style={{ padding: 9 }}>
						<Input
							error={error}
							autoFocus
							ref={inputRef}
							placeholder="Enter entity value"
							onKeyDown={(e) => {
								e.persist();
								handleKeyDown(e);
							}}
							onChange={() => setError('')}
							close={() => {
								setIsAdding(false);
								setError('');
							}}
						/>
					</div>
				) : (
				<button
					className={style.add}
					onClick={() => {
						setEdit({
							index: -1,
							initialValue: '',
						});
						setIsAdding(true);
						setError('');
					}}
				>
					<img src={AddIcon} alt="add value" />
					<span>Add Value</span>
				</button>
				)}
				</>
			 )}
				<div className={style.values}>
					{list.map((value, i) => {
						if (deleteList.includes(i)) {
							return null;
						}
						return (
							<div className={style.valueWrapper} key={value}>
								<div className={style.value} data-edit={edit.index === i}>
									<p>{updateList[i] ?? value}</p>
									<div className={style.editDelete}>
										{canEdit && (
											<button
												onClick={() => {
													setIsAdding(false);
													setEdit({
														initialValue: value,
														index: i,
													});
													setError('');
												}}
											>
												<EditIcon width="13" height="13" stroke="#292D32" />
											</button>
										)}
										{canDelete && (
											<button onClick={() => handleDelete(i)}>
												<DeleteIcon width="13" height="13" stroke="#292D32" />
											</button>
										)}
									</div>
								</div>
								{edit.index === i && (
									<div style={{ margin: '0 12px 8px 12px' }}>
										<Input
											error={error}
											defaultValue={edit.initialValue}
											autoFocus
											ref={inputRef}
											placeholder="Enter entity value"
											onKeyDown={(e) => {
												e.persist();
												handleKeyDown(e);
											}}
											onChange={() => setError('')}
											close={() => {
												setEdit({ initialValue: '', index: -1 });
												setError('');
											}}
										/>
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	)
}
